/**
 * analyze — client helpers for the YouTube-Shorts analysis pipeline.
 *
 * All calls go to the same-origin BFF (`/api/*`), which relays to the
 * Cloudflare Worker with the session cookie attached. Phase 25-AE.
 */

export type AnalyzeMode =
	| "ai-category"
	| "ai-self"
	| "auto-recommend"
	| "manual";

/** Map a Welcome-card mode chip to the Worker's `target_clip_count` field. */
export function modeToClipCount(mode: AnalyzeMode): number | "auto" {
	switch (mode) {
		case "ai-category":
		case "ai-self":
			return "auto";
		case "auto-recommend":
		case "manual":
			return 6;
		default:
			return 6;
	}
}

/** Korean chip label -> AnalyzeMode key. */
export function chipLabelToMode(label: string): AnalyzeMode {
	switch (label) {
		case "AI + 카테고리 자동":
			return "ai-category";
		case "AI 자체 판단":
			return "ai-self";
		case "자동 추천":
			return "auto-recommend";
		case "수동":
			return "manual";
		default:
			return "ai-category";
	}
}

export interface CreateJobInput {
	source_url: string;
	source_kind?: "youtube" | "local";
	source_title?: string;
	/** For source_kind="local" (Helper download path) — recorded on the job. */
	source_filename?: string;
	target_clip_count?: number | "auto";
}

export interface CreateJobResult {
	ok: boolean;
	jobId?: string;
	/** machine-readable error: "unauthenticated" | "insufficient_credits" | ... */
	error?: string;
	status?: number;
	balance?: number;
	required?: number;
}

/**
 * POST /api/youtube-shorts/jobs — create an analysis job.
 * 401 -> { ok:false, error:"unauthenticated" } (caller should redirect to /login)
 * 402 -> { ok:false, error:"insufficient_credits", balance, required }
 */
export async function createJob(input: CreateJobInput): Promise<CreateJobResult> {
	try {
		const res = await fetch("/api/youtube-shorts/jobs", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source_kind: input.source_kind ?? "youtube",
				source_url: input.source_url,
				source_title: input.source_title,
				source_filename: input.source_filename,
				target_clip_count: input.target_clip_count ?? "auto",
			}),
		});
		const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
		console.log("[analyze] createJob response", { status: res.status, data });

		if (res.status === 202 || res.ok) {
			return { ok: true, jobId: data.job_id as string, status: res.status };
		}
		return {
			ok: false,
			status: res.status,
			error: (data.error as string) ?? `http_${res.status}`,
			balance: data.balance as number | undefined,
			required: data.required as number | undefined,
		};
	} catch (e) {
		console.error("[analyze] createJob failed", e);
		return { ok: false, error: "network_error" };
	}
}

/**
 * Upload a Helper-downloaded audio file to a job, which forwards it to the
 * FastAPI analysis pipeline (POST /api/youtube-shorts/jobs/:id/analyze, the
 * multipart audio-upload endpoint). Used by the Helper download path.
 */
export async function uploadAudioToJob(
	jobId: string,
	audio: ArrayBuffer,
	filename: string,
	meta: { source_title?: string; target_clip_count?: number | "auto" },
): Promise<boolean> {
	try {
		const form = new FormData();
		form.append("audio", new Blob([audio]), filename);
		form.append(
			"meta_json",
			JSON.stringify({
				source_title: meta.source_title ?? "",
				source_duration_sec: 0,
				target_clip_count:
					typeof meta.target_clip_count === "number"
						? meta.target_clip_count
						: 6,
			}),
		);
		const res = await fetch(`/api/youtube-shorts/jobs/${jobId}/analyze`, {
			method: "POST",
			body: form,
		});
		console.log("[analyze] uploadAudioToJob", {
			jobId,
			status: res.status,
			bytes: audio.byteLength,
		});
		return res.ok || res.status === 202;
	} catch (e) {
		console.error("[analyze] uploadAudioToJob failed", e);
		return false;
	}
}

export interface CreditsInfo {
	balance: number;
	tier: string;
}

/** GET /api/credits — current balance + tier. null when not logged in / error. */
export async function getCredits(): Promise<CreditsInfo | null> {
	try {
		const res = await fetch("/api/credits", { cache: "no-store" });
		if (!res.ok) return null;
		const data = (await res.json()) as { balance?: number; tier?: string };
		return {
			balance: Number(data.balance ?? 0),
			tier: data.tier ?? "free",
		};
	} catch {
		return null;
	}
}
